import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import axios from 'axios';
import { Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { useAuth, TwoFactorRequiredError } from '../contexts/AuthContext';

interface LoginForm {
  email: string;
  password: string;
}

export function LoginPage() {
  const navigate = useNavigate();
  const { login, verifyTwoFactor } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [twoFactorEmail, setTwoFactorEmail] = useState<string | null>(null);
  const [twoFactorToken, setTwoFactorToken] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>();

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true);
    try {
      await login(data.email, data.password);
      toast.success('Login realizado com sucesso!');
      navigate('/');
    } catch (error: unknown) {
      if (error instanceof TwoFactorRequiredError) {
        setTwoFactorEmail(error.email);
        toast('Código 2FA necessário', { icon: '🔐' });
      } else {
        const message = axios.isAxiosError(error)
          ? error.response?.data?.error || 'Erro ao fazer login'
          : 'Erro ao fazer login';
        toast.error(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onVerifyTwoFactor = async () => {
    if (!twoFactorEmail || twoFactorToken.length !== 6) {
      return;
    }
    setIsLoading(true);
    try {
      await verifyTwoFactor(twoFactorEmail, twoFactorToken);
      toast.success('Login realizado com sucesso!');
      navigate('/');
    } catch (error: unknown) {
      const message = axios.isAxiosError(error)
        ? error.response?.data?.error || 'Código 2FA inválido'
        : 'Código 2FA inválido';
      toast.error(message);
      setTwoFactorToken('');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-ink-primary p-4">
      <div className="w-full max-w-md bg-surface rounded-[14px] shadow-xl p-8">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-ink-primary tracking-wider">CRM SaaS</h1>
          <p className="text-sm text-ink-tertiary mt-1">
            Gerencie suas vendas de forma inteligente
          </p>
        </div>

        {twoFactorEmail ? (
          <>
            <div className="flex items-center gap-3 mb-2">
              <ShieldCheck className="w-8 h-8 text-ink-primary" />
              <h2 className="text-2xl font-bold text-ink-primary">Verificação 2FA</h2>
            </div>
            <p className="text-ink-secondary mb-8">
              Digite o código de 6 dígitos do seu aplicativo autenticador
            </p>

            <div className="space-y-6">
              <div>
                <label htmlFor="totp" className="label">
                  Código de Verificação
                </label>
                <input
                  id="totp"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  className="input text-center text-2xl tracking-widest"
                  placeholder="000000"
                  value={twoFactorToken}
                  onChange={(e) => setTwoFactorToken(e.target.value.replace(/\D/g, ''))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      onVerifyTwoFactor();
                    }
                  }}
                  autoFocus
                />
              </div>

              <button
                type="button"
                disabled={isLoading || twoFactorToken.length !== 6}
                className="bg-ink-primary hover:bg-ink-secondary text-canvas font-medium py-2.5 rounded-[10px] transition-all w-full disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={onVerifyTwoFactor}
              >
                {isLoading ? 'Verificando...' : 'Verificar'}
              </button>

              <button
                type="button"
                className="w-full text-sm text-ink-tertiary hover:text-ink-primary"
                onClick={() => {
                  setTwoFactorEmail(null);
                  setTwoFactorToken('');
                }}
              >
                Voltar ao login
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-2xl font-bold text-ink-primary mb-2">Bem-vindo de volta</h2>
            <p className="text-ink-secondary mb-8">
              Entre com suas credenciais para acessar o sistema
            </p>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div>
                <label htmlFor="email" className="label">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  className="input"
                  placeholder="seu@email.com"
                  {...register('email', {
                    required: 'Email é obrigatório',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Email inválido',
                    },
                  })}
                />
                {errors.email && <p className="mt-1 text-sm text-danger">{errors.email.message}</p>}
              </div>

              <div>
                <label htmlFor="password" className="label">
                  Senha
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    className="input pr-10"
                    placeholder="Sua senha"
                    {...register('password', {
                      required: 'Senha é obrigatória',
                      minLength: {
                        value: 8,
                        message: 'Senha deve ter no mínimo 8 caracteres',
                      },
                    })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-tertiary hover:text-ink-primary"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-danger">{errors.password.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="bg-ink-primary hover:bg-ink-secondary text-canvas font-medium py-2.5 rounded-[10px] transition-all w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Entrando...' : 'Entrar'}
              </button>
            </form>
          </>
        )}

        {/* Footer */}
        <p className="text-center text-xs text-ink-tertiary mt-8">
          &copy; {new Date().getFullYear()} CRM SaaS
        </p>
      </div>
    </div>
  );
}

export default LoginPage;
